import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Search, Wand2, X } from "lucide-react";
import api from "@/lib/api";

export default function SeoManager() {
  const [audit, setAudit] = useState(null);
  const [seo, setSeo] = useState({ site_title: "", site_description: "", keywords: "", og_image: "" });
  const [saving, setSaving] = useState(false);
  const [fixing, setFixing] = useState(false);

  const load = () => {
    api.get("/admin/seo/audit").then((r) => setAudit(r.data)).catch(() => {});
    api
      .get("/settings")
      .then((r) => setSeo({ site_title: "", site_description: "", keywords: "", og_image: "", ...(r.data?.seo || {}) }))
      .catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { seo });
      toast.success("Réglages SEO enregistrés.");
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const applyFixes = async () => {
    setFixing(true);
    try {
      const { data } = await api.post("/admin/seo/apply-fixes");
      toast.success(
        data.applied.length > 0
          ? `Corrections appliquées : ${data.applied.join(", ")}.`
          : "Aucune correction automatique nécessaire."
      );
      load();
    } catch {
      toast.error("Erreur lors de l'application des corrections.");
    } finally {
      setFixing(false);
    }
  };

  const scoreColor = !audit ? "text-ivory" : audit.score >= 80 ? "text-gold" : audit.score >= 50 ? "text-copper" : "text-red-400";

  return (
    <div className="max-w-4xl" data-testid="seo-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Assistant SEO</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Analyse automatique du site, score et recommandations. Le sitemap
            (/api/sitemap.xml) et les données structurées sont générés automatiquement.
          </p>
        </div>
        <button onClick={applyFixes} disabled={fixing} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="seo-apply-fixes">
          <Wand2 className="h-4 w-4" strokeWidth={1.5} />
          {fixing ? "Application…" : "Appliquer les corrections automatiques"}
        </button>
      </div>

      {audit && (
        <>
          <div className="mb-8 flex items-center gap-8 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="seo-score-panel">
            <div className="text-center">
              <p className={`text-5xl font-bold tracking-tight ${scoreColor}`} data-testid="seo-score">
                {audit.score}
              </p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-ivory/40">Score SEO / 100</p>
            </div>
            <div className="h-16 w-px bg-white/10" />
            <div className="flex-1">
              <div className="h-2.5 overflow-hidden rounded-full bg-white/5">
                <div
                  className={`h-full rounded-full ${audit.score >= 80 ? "bg-gold" : audit.score >= 50 ? "bg-copper" : "bg-red-400"}`}
                  style={{ width: `${audit.score}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-ivory/50">
                {audit.checks.filter((c) => c.ok).length} contrôles réussis sur {audit.checks.length}
              </p>
            </div>
          </div>

          <div className="mb-10 space-y-2" data-testid="seo-checks">
            {audit.checks.map((check) => (
              <div key={check.key} className="flex items-start gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`seo-check-${check.key}`}>
                {check.ok ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                ) : (
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400" strokeWidth={1.5} />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${check.ok ? "text-ivory/70" : "font-semibold text-ivory"}`}>
                    {check.label}
                  </p>
                  {!check.ok && check.recommendation && (
                    <p className="mt-1 text-xs leading-relaxed text-ivory/45">{check.recommendation}</p>
                  )}
                </div>
                {check.auto_fixable && (
                  <span className="shrink-0 rounded border border-gold/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold">
                    correction auto
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="seo-settings">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          <Search className="h-4 w-4" strokeWidth={1.5} />
          Balises du site
        </h3>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
            Titre du site (50-60 caractères)
          </label>
          <input value={seo.site_title} onChange={(e) => setSeo((s) => ({ ...s, site_title: e.target.value }))} className="input-dark !h-11" data-testid="seo-title-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
            Meta description (140-160 caractères)
          </label>
          <textarea rows={3} value={seo.site_description} onChange={(e) => setSeo((s) => ({ ...s, site_description: e.target.value }))} className="input-dark !h-auto py-3" data-testid="seo-description-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
            Mots-clés (séparés par des virgules)
          </label>
          <input value={seo.keywords} onChange={(e) => setSeo((s) => ({ ...s, keywords: e.target.value }))} placeholder="restaurant africain dijon, épicerie africaine, ménage dijon…" className="input-dark !h-11" data-testid="seo-keywords-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
            Image de partage (URL — vide = image d'accueil)
          </label>
          <input value={seo.og_image} onChange={(e) => setSeo((s) => ({ ...s, og_image: e.target.value }))} placeholder="https://…" className="input-dark !h-11" data-testid="seo-ogimage-input" />
        </div>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="seo-save-button">
          {saving ? "Enregistrement…" : "Enregistrer les balises"}
        </button>
      </div>
    </div>
  );
}
